export const KANBAN_GUIDE = {
  id: "kanban-guide",
  title: "Kanban & Operating Cadence",
  description:
    "Manage your operating cadence with kanban boards, cadence stages, and a calendar view. Track tasks, client engagements, and events with drag-and-drop, recurrence, and due dates.",
  icon: "LayoutGrid",
  version: "1.0.0",
  lastUpdated: "2026-03-16",
  roles: ["admin", "exec"],
  sections: [
    {
      id: "overview",
      title: "Overview",
      content: [
        {
          type: "paragraph",
          text: "The **Kanban** page is an operating cadence tool for tracking tasks, client engagements, and events. It provides three switchable views of the same data:",
        },
        {
          type: "steps",
          items: [
            "**Kanban** — a customisable board with columns you define (e.g. Backlog, This Week, In Progress, Done)",
            "**Cadence** — a fixed set of stages: Planning, Scheduled, In Progress, Complete, Review",
            "**Calendar** — a month/week grid showing items by due date, with recurring items expanded",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The Kanban page is only visible to **Admin** and **Exec** roles. Other roles will not see it in the sidebar.",
        },
      ],
    },

    {
      id: "boards",
      title: "Managing Boards",
      content: [
        {
          type: "paragraph",
          text: "You can create multiple boards to organise different workstreams — for example, one board for client engagements and another for internal operations.",
        },
        {
          type: "heading",
          level: 3,
          text: "Creating a Board",
        },
        {
          type: "steps",
          items: [
            "Click the **+ New Board** button in the top-left",
            "Enter a name for the board (e.g. \"Client Engagement\")",
            "The board is created with default columns: Backlog, This Week, In Progress, and Done",
          ],
        },
        {
          type: "heading",
          level: 3,
          text: "Switching Boards",
        },
        {
          type: "paragraph",
          text: "Use the **dropdown** in the top-left to switch between boards. The selected board persists while you switch between Kanban, Cadence, and Calendar views.",
        },
        {
          type: "heading",
          level: 3,
          text: "Deleting a Board",
        },
        {
          type: "paragraph",
          text: "Click the **trash icon** next to the board dropdown. This deletes the board and **all its items** permanently. You will be asked to confirm before deletion.",
        },
        {
          type: "callout",
          variant: "warning",
          text: "Deleting a board cannot be undone. All items on the board will be permanently removed.",
        },
      ],
    },

    {
      id: "views",
      title: "The Three Views",
      content: [
        {
          type: "paragraph",
          text: "Switch between views using the **Kanban**, **Cadence**, and **Calendar** tabs at the top of the page. All three views show items from the same board — changes in one view are reflected in the others.",
        },
        {
          type: "heading",
          level: 3,
          text: "Kanban View",
        },
        {
          type: "paragraph",
          text: "The default view. Items are organised into columns that you define when creating the board. Drag and drop cards between columns or reorder within a column.",
        },
        {
          type: "heading",
          level: 3,
          text: "Cadence View",
        },
        {
          type: "paragraph",
          text: "A fixed set of stages designed for recurring operating cadence workflows. The columns are always: **Planning**, **Scheduled**, **In Progress**, **Complete**, and **Review**. These cannot be customised — use the Kanban view if you need custom columns.",
        },
        {
          type: "heading",
          level: 3,
          text: "Calendar View",
        },
        {
          type: "paragraph",
          text: "A month or week grid showing items positioned by their **due date**. Recurring items are expanded to show each occurrence. Toggle between month and week views using the buttons in the top-right.",
        },
        {
          type: "steps",
          items: [
            "Use the **arrow buttons** to navigate between months/weeks",
            "Click **Today** to jump back to the current date",
            "Click a **date cell** to quick-add an item on that date",
            "Click a **card** to open the detail modal",
          ],
        },
      ],
    },

    {
      id: "items",
      title: "Working with Items",
      content: [
        {
          type: "heading",
          level: 3,
          text: "Item Types",
        },
        {
          type: "paragraph",
          text: "Each item has a **type** that determines its colour coding:",
        },
        {
          type: "table",
          headers: ["Type", "Colour", "Use For"],
          rows: [
            ["Task", "Blue", "Internal to-dos, follow-ups, action items"],
            ["Engagement", "Purple", "Client meetings, advisory sessions, relationship touchpoints"],
            ["Event", "Cyan", "External events, conferences, webcasts, deadlines"],
          ],
        },
        {
          type: "heading",
          level: 3,
          text: "Adding Items",
        },
        {
          type: "paragraph",
          text: "In Kanban or Cadence view, click the **+** button at the bottom of any column. Enter a title and press Enter or click Add. The item is created in that column.",
        },
        {
          type: "heading",
          level: 3,
          text: "Editing Items",
        },
        {
          type: "paragraph",
          text: "Click any card to open the **detail modal**. Here you can edit:",
        },
        {
          type: "steps",
          items: [
            "**Title** and **description**",
            "**Type** — Task, Engagement, or Event",
            "**Column** — move the item to a different column",
            "**Priority** — High (red), Medium (amber), or Low (grey)",
            "**Assigned to** — who is responsible",
            "**Due date** — when it's due",
            "**Recurrence** — set a repeating schedule (see below)",
          ],
        },
        {
          type: "heading",
          level: 3,
          text: "Drag and Drop",
        },
        {
          type: "paragraph",
          text: "Drag cards between columns to change their status. You can also drag cards within a column to reorder them. The new position is saved automatically.",
        },
        {
          type: "heading",
          level: 3,
          text: "Due Date Indicators",
        },
        {
          type: "paragraph",
          text: "Cards show colour-coded due date badges:",
        },
        {
          type: "table",
          headers: ["Colour", "Meaning"],
          rows: [
            ["Red", "Overdue"],
            ["Amber", "Due today"],
            ["Blue", "Due this week"],
            ["Grey", "Due later"],
          ],
        },
      ],
    },

    {
      id: "recurrence",
      title: "Recurring Items",
      content: [
        {
          type: "paragraph",
          text: "Items can be set to recur on a schedule. This is useful for regular cadence activities like monthly client reviews or weekly team check-ins.",
        },
        {
          type: "heading",
          level: 3,
          text: "Setting Recurrence",
        },
        {
          type: "steps",
          items: [
            "Open an item by clicking its card",
            "In the **Recurrence** field, select a preset from the dropdown",
            "Click **Save**",
          ],
        },
        {
          type: "heading",
          level: 3,
          text: "Available Presets",
        },
        {
          type: "table",
          headers: ["Preset", "Schedule"],
          rows: [
            ["Daily", "Every day"],
            ["Weekly on Monday", "Every Monday"],
            ["Weekly on Wednesday", "Every Wednesday"],
            ["Weekly on Friday", "Every Friday"],
            ["1st Monday of month", "First Monday each month"],
            ["2nd Wednesday of month", "Second Wednesday each month"],
            ["4th Wednesday of month", "Fourth Wednesday each month"],
            ["15th of month", "15th of each month"],
            ["Last day of month", "28th of each month"],
          ],
        },
        {
          type: "heading",
          level: 3,
          text: "How Recurrence Works",
        },
        {
          type: "paragraph",
          text: "Recurring items appear as **virtual cards** on the calendar at each occurrence. The original item stays on the kanban board in its column. In the calendar view, you'll see the item repeated on each scheduled date within the visible range.",
        },
        {
          type: "callout",
          variant: "info",
          text: "Recurrence uses standard RRULE format (RFC 5545). Advanced users can enter custom rules directly, e.g. `FREQ=WEEKLY;BYDAY=MO,WE,FR` for Mon/Wed/Fri.",
        },
      ],
    },

    {
      id: "tips",
      title: "Tips & Best Practices",
      content: [
        {
          type: "steps",
          items: [
            "**Use boards for workstreams** — create separate boards for client engagement, internal ops, and events rather than mixing everything on one board",
            "**Set due dates** — items without due dates won't appear on the calendar view",
            "**Use Cadence view for recurring workflows** — the fixed stages (Planning → Scheduled → In Progress → Complete → Review) work well for repeating processes like monthly client reviews",
            "**Use Kanban view for project work** — custom columns give you flexibility for ad-hoc workflows",
            "**Check the Calendar weekly** — the week view gives a focused look at what's coming up",
          ],
        },
      ],
    },
  ],
};
